import { Box, Carousel, IconButton, Text, TextButton } from '@wix/design-system';
import { ChevronLeftSmall, ChevronRightSmall } from '@wix/wix-ui-icons-common/odeditor';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import React, { useRef, useState } from 'react';
import { DomainSearchGateModule } from '../../domainSearchGate.module';
import { useTldOffersData } from '../useTldOffersData';
import { TldOfferCard, TldOfferCardSkeleton } from '../card';
import { CAROUSEL_CHUNK_SIZE, TLD_OFFER_WIDGET_BI_ORIGIN, TLD_OFFER_WIDGET_BI_TYPE, TldStripComponentName } from '../tldOfferWidget.constants';
import { TldOfferWidgetModule } from '../tldOfferWidget.module';
import { shouldLoadNextBatchForSlide } from '../tldOfferWidget.utils';
import { useTldStripImpression } from '../useTldStripImpression';
import { TldOfferCarouselSlide } from './TldOfferCarouselSlide';
export const tldOfferCarouselDataHooks = {
  root: 'tld-offer-carousel',
  previous: 'tld-offer-carousel-previous',
  next: 'tld-offer-carousel-next',
  card: 'tld-offer-carousel-card',
  skeleton: 'tld-offer-carousel-skeleton',
  viewAll: 'tld-offer-carousel-view-all',
  hideAll: 'tld-offer-carousel-hide-all'
};
export const TldOfferCarousel = _ref => {
  let domain = _ref.domain;
  const carouselRef = useRef(null);
  const _useState = useState(0),
    index = _useState[0],
    setIndex = _useState[1];
  const _useState2 = useState(false),
    isOpenedByUser = _useState2[0],
    setIsOpenedByUser = _useState2[1];
  const _useTldOffersData = useTldOffersData(domain),
    sld = _useTldOffersData.sld,
    offers = _useTldOffersData.offers,
    isLoading = _useTldOffersData.isLoading,
    isFolded = _useTldOffersData.isFolded,
    isEligible = _useTldOffersData.isEligible,
    hasNextPage = _useTldOffersData.hasNextPage,
    isFetchingNextPage = _useTldOffersData.isFetchingNextPage,
    fetchNextPage = _useTldOffersData.fetchNextPage,
    pricesByDomain = _useTldOffersData.pricesByDomain,
    isPricingLoading = _useTldOffersData.isPricingLoading;
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.useSuggestionActionResolver(),
    resolveActionForSuggestion = _DomainSearchSuiteMod.resolveActionForSuggestion;
  const CarouselStripContainer = TldOfferWidgetModule.SDK.Layouts.CarouselStripContainer;
  const isOpen = !isFolded || isOpenedByUser;
  const viewedTlds = offers.slice(index * CAROUSEL_CHUNK_SIZE, (index + 1) * CAROUSEL_CHUNK_SIZE);
  const impressionRef = useTldStripImpression({
    domain,
    sld,
    componentName: TldStripComponentName.CAROUSEL,
    pageNumber: index + 1,
    tlds: viewedTlds,
    enabled: !isLoading && isEligible && isOpen && viewedTlds.length > 0
  });
  if (!isLoading && !isEligible) {
    return null;
  }
  const isAtLoadedEnd = (index + 1) * CAROUSEL_CHUNK_SIZE >= offers.length;
  const skeletonSlides = Array.from({
    length: CAROUSEL_CHUNK_SIZE + 1
  }, (_, slideIndex) => /*#__PURE__*/React.createElement(TldOfferCarouselSlide, {
    key: `skeleton-${slideIndex}`
  }, /*#__PURE__*/React.createElement(TldOfferCardSkeleton, {
    dataHook: tldOfferCarouselDataHooks.skeleton
  })));
  const handleGetItClick = async (tld, position) => {
    DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
      domain: `${sld}.${tld}`,
      position,
      type: TLD_OFFER_WIDGET_BI_TYPE,
      origin: TLD_OFFER_WIDGET_BI_ORIGIN
    });
    await resolveActionForSuggestion(`${sld}.${tld}`, false);
  };
  const handleSlide = nextIndex => {
    setIndex(nextIndex);
    if (shouldLoadNextBatchForSlide({
      chunkIndex: nextIndex,
      offersCount: offers.length,
      hasMore: hasNextPage,
      isFetching: isFetchingNextPage
    })) {
      fetchNextPage();
    }
  };
  return /*#__PURE__*/React.createElement(CarouselStripContainer, {
    containerRef: impressionRef,
    dataHook: tldOfferCarouselDataHooks.root
  }, /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle",
    gap: "18px",
    padding: "0 SP6",
    position: "relative",
    zIndex: 1
  }, /*#__PURE__*/React.createElement(Box, {
    flexGrow: 1
  }, /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    weight: "bold"
  }, t('domains_search_gate.results_optimisations.tld_strip.title', {
    domainSLD: sld
  }))), isFolded && /*#__PURE__*/React.createElement(TextButton, {
    size: "medium",
    skin: "dark",
    underline: "always",
    onClick: () => {
      setIsOpenedByUser(!isOpenedByUser);
      setIndex(0);
    },
    dataHook: isOpen ? tldOfferCarouselDataHooks.hideAll : tldOfferCarouselDataHooks.viewAll
  }, t(isOpen ? 'domains_search_gate.results_optimisations.tld_strip.hide_all' : 'domains_search_gate.results_optimisations.tld_strip.view_all')), isOpen && /*#__PURE__*/React.createElement(Box, {
    gap: "6px"
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    priority: "secondary",
    skin: "dark",
    disabled: isLoading || index === 0,
    onClick: () => {
      var _carouselRef$current;
      return (_carouselRef$current = carouselRef.current) == null ? void 0 : _carouselRef$current.slideTo(Math.max(index - 1, 0));
    },
    dataHook: tldOfferCarouselDataHooks.previous
  }, /*#__PURE__*/React.createElement(ChevronLeftSmall, null)), /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    priority: "secondary",
    skin: "dark",
    disabled: isLoading || isAtLoadedEnd && (!hasNextPage || isFetchingNextPage),
    onClick: () => {
      var _carouselRef$current2;
      return (_carouselRef$current2 = carouselRef.current) == null ? void 0 : _carouselRef$current2.slideTo(index + 1);
    },
    dataHook: tldOfferCarouselDataHooks.next
  }, /*#__PURE__*/React.createElement(ChevronRightSmall, null)))), isOpen && /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "SP6",
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Carousel, {
    ref: carouselRef,
    variableWidth: true,
    slideSpacing: 16,
    slidingType: "reveal-chunk",
    controlsPosition: "none",
    dots: false,
    beforeChange: (_fromIndex, toIndex) => handleSlide(toIndex)
  }, isLoading ? skeletonSlides : [...offers.map((tld, position) => /*#__PURE__*/React.createElement(TldOfferCarouselSlide, {
    key: tld
  }, /*#__PURE__*/React.createElement(TldOfferCard, {
    domainName: `${sld}.${tld}`,
    pricing: pricesByDomain.get(`${sld}.${tld}`),
    isPricingLoading: isPricingLoading,
    onGetItClick: () => handleGetItClick(tld, position + 1),
    dataHook: tldOfferCarouselDataHooks.card
  }))), ...(isFetchingNextPage ? skeletonSlides : [])])));
};
//# sourceMappingURL=TldOfferCarousel.js.map